import Element from './Element';
import ClickableMixin from './mixins/ClickableMixin';
import ContainableMixin from './mixins/ContainableMixin';
import * as members from '../privates/privateMembers';
import mixin from '../privates/utils/mixin';

/**
 * @class Page
 * @summary A page is a web page that can contain other elements.
 * @classdesc
 *  Some `Page` properties refer only to the central part of the web page as show here:
 *
 * ![Wix Page](images/page.png "Wix Page")
 *
 *  For example, elements placed within this central area are the page's
 *  [children](#children), but elements placed in the page
 *  [header]($w.Header.html) are not.
 *
 *  Other `Page` properties refer to the page in general.
 * 
 *  The `Page` is part of the [`Document`]($w.Document.html). 
 * 
 * When the page has loaded and is ready for site visitor interaction, the [`onReady`]($w/onready) event will fire.
 * @see [Header]($w.Header.html)
 * @see [Footer]($w.Footer.html)
 * @see [Document]($w.Document.html)
 * @mixes $w.Element
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @queryable
 * @viewername mobile.core.components.Page
 * @definitionId mobile.core.components.Page
 * @tagname page
 */

class Page extends Element {

    /**
     * @member description
     * @label property
     * @summary Gets the page's [SEO description](https://support.wix.com/en/article/adding-seo-page-titles-and-descriptions-meta-tags).
     * @description
     *  The page description is a meta tag. It provides potential site visitors
     *  with a summary of what the page is about. It is not visible to users,
     *  but appears below your site's title in search results.
     *
     *  A page's `description` is [set in the Editor](https://support.wix.com/en/article/changing-your-page-description)
     *  using the [Page Settings panel](https://support.wix.com/en/article/page-settings).
     * @snippet [Page-description_get.es6=Get the page's description]
     * @type {String}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get description() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentPageSEODescription;
    }

    /**
     * @member keywords
     * @label property
     * @summary Gets the page's keywords.
     * @description
     *  The [page's keywords](https://support.wix.com/en/article/page-keywords)
     *  are not used by Google, but may be used by other search engines.
     *
     *  Page `keywords` are set in the Editor using the
     *  [Page Settings panel](https://support.wix.com/en/article/page-settings).
     * @snippet [Page-keywords_get.es6=Get the page's description]
     * @type {String[]}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get keywords() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentPageSEOKeywords;
    }

    /**
     * @member {String} title
     * @label property
     * @summary Gets the page's title.
     * @description
     *  The [page title](https://support.wix.com/en/article/adding-seo-page-titles-and-descriptions-meta-tags#page-titles) is used in:
     *
     *  + Search results.
     *  + When saving to favorites.
     *  + In the browser's tab.
     *
     *
     *  A page's `title` is [set in the Editor](https://support.wix.com/en/article/adding-seo-page-titles-and-descriptions-meta-tags#page-titles)
     *  using the [Page Settings panel](https://support.wix.com/en/article/page-settings).
     *  You can also set a page's title using the [`setTitle()`](wix-seo.html#setTitle) function.
     * 
     * > **Note:** The `title` in Preview mode is different than the `title` of the published site.
     * @snippet [Page-title_get.es6=Get the page's title]
     * @memberof $w.Page
     * @see [setTitle( )](wix-seo.html#setTitle)
     * @instance
     * @readonly
     */
    get title() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentUrlPageTitle;
    }

    /**
     * @member visibleInMenu
     * @label property
     * @summary Indicates if the page is included in the site's menu.
     * @description
     *  Whether a page is visible in the site menu is [set in the Editor](https://www.wix.com/support/html5/article/hiding-a-page-from-the-menu)
     *  using the [Page Settings panel](https://support.wix.com/en/article/page-settings).
     * @snippet [Page-visibleInMenu_get.es6=Get whether the page appears in the site menu]
     * @type {Boolean}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get visibleInMenu() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const pageData = RMI.getData(compId);
        if (pageData) {
            return !pageData.hidePage;
        }
    }

    toJSON() {
        const {id, type, global, rendered, title, description, url, fullUrl, keywords, visibleInMenu} = this;
        return {id, type, global, rendered, title, description, url, fullUrl, keywords, visibleInMenu};
    }
}

mixin(Page.prototype, ClickableMixin.prototype);
mixin(Page.prototype, ContainableMixin.prototype);

export default Page;
